if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.preorderNotificationHint = this.querySelector('[data-preorder-notification-hint]');
      }

      connectedCallback() {
        if (!this.preorderNotificationHint) return;
        const scope = this.closest('product-info') || this.parentElement;
        this.preorderNotificationObserver = new MutationObserver(() => this.updatePreorderNotificationHint());
        this.preorderNotificationObserver.observe(scope, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-hidden'],
        });
        this.updatePreorderNotificationHint();
      }

      disconnectedCallback() {
        this.preorderNotificationObserver?.disconnect();
      }

      updatePreorderNotificationHint() {
        if (!this.preorderNotificationHint) return;
        const scope = this.closest('product-info') || this.parentElement;
        const notifyButton = scope?.querySelector('.restock-rocket-button-product');
        const notifyStyle = notifyButton ? window.getComputedStyle(notifyButton) : null;
        const canNotify = notifyButton && !notifyButton.disabled &&
          notifyButton.getClientRects().length > 0 &&
          notifyStyle.display !== 'none' && notifyStyle.visibility !== 'hidden';
        const showHint = Boolean(this.submitButton.disabled && canNotify);
        if (this.preorderNotificationHint.hidden !== !showHint) this.preorderNotificationHint.hidden = !showHint;
        if (this.hasAttribute('data-preorder-notify-active') !== showHint) this.toggleAttribute('data-preorder-notify-active', showHint);
        if (showHint && this.hasAttribute('data-preorder-notification-target') && window.location.hash === '#benachrichtigung') {
          const notificationUrl = new URL(window.location.href);
          notificationUrl.hash = '';
          window.history.replaceState(window.history.state, '', notificationUrl);
          notifyButton.focus({ preventScroll: true });
          notifyButton.click();
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        const quantity = this.form.elements.namedItem('quantity');
        if (quantity?.dataset.sharedStock === 'true') {
          if (this.form.dataset.sharedStockLoading === 'true') return;
          const value = Number(quantity.value);
          const minimum = Math.max(1, Number(quantity.dataset.min) || 1);
          const maximum = quantity.max === '' ? Infinity : Number(quantity.max);
          const step = Number(quantity.step) || 1;
          let message = '';
          if (!Number.isInteger(value) || value < minimum) {
            message = window.quickOrderListStrings.min_error.replace('[min]', minimum);
          } else if (value > maximum) {
            message = maximum < minimum ? this.soldOutLabel : window.quickOrderListStrings.max_error.replace('[max]', maximum);
          } else if ((value - minimum) % step !== 0) {
            message = window.quickOrderListStrings.step_error.replace('[step]', step);
          }
          quantity.setCustomValidity(message);
          if (message) {
            this.handleErrorMessage(message);
            quantity.reportValidity();
            return;
          }
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text === window.variantStrings.soldOut ? this.soldOutLabel : text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
        this.updatePreorderNotificationHint();
      }

      get soldOutLabel() {
        return this.dataset.soldOutLabel || window.variantStrings.soldOut;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}



